import React, { useEffect, useState } from "react";
import {
  Loader2, Phone, Clock, PhoneCall, TrendingUp, DollarSign, CalendarCheck, BarChart3,
  Sparkles, Wallet, Search, ListFilter,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import { aiAgentsApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";

const err = (e, fb) => toast.error(formatApiErrorDetail(e?.response?.data?.detail) || fb);
const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const OUTCOME_COLORS = {
  estimate_accepted: "bg-emerald-500", appointment_scheduled: "bg-indigo-500",
  requested_callback: "bg-sky-500", transferred: "bg-violet-500", not_interested: "bg-rose-400",
  voicemail_left: "bg-amber-400", no_answer: "bg-slate-300", wrong_number: "bg-slate-400",
  do_not_call: "bg-red-500",
};

const KPIS = [
  { k: "callsMade", label: "Calls made", icon: Phone, fmt: (v) => v, cls: "text-slate-700" },
  { k: "minutesUsed", label: "Minutes used", icon: Clock, fmt: (v) => v, cls: "text-blue-600" },
  { k: "connectionRate", label: "Connection rate", icon: PhoneCall, fmt: (v) => `${v}%`, cls: "text-cyan-600" },
  { k: "conversionRate", label: "Conversion rate", icon: TrendingUp, fmt: (v) => `${v}%`, cls: "text-emerald-600" },
  { k: "revenueClosed", label: "Revenue closed", icon: DollarSign, fmt: usd, cls: "text-emerald-700" },
  { k: "appointmentsScheduled", label: "Appointments", icon: CalendarCheck, fmt: (v) => v, cls: "text-violet-600" },
];

export default function Reports({ agents }) {
  const [days, setDays] = useState(30);
  const [agentId, setAgentId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [summariesLoading, setSummariesLoading] = useState(true);
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = { days };
      if (agentId) params.agentId = agentId;
      setData(await aiAgentsApi.reports(params));
    } catch (e) { err(e, "Failed to load reports."); }
    finally { setLoading(false); }
  };
  useEffect(() => {
    let active = true;
    (async () => { await Promise.resolve(); if (active) load(); })();
    return () => { active = false; };
  }, [days, agentId]);

  const loadSummaries = async () => {
    setSummariesLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;
      setSummaries(await aiAgentsApi.callSummaries(params));
    } catch (e) { err(e, "Failed to load call summaries."); }
    finally { setSummariesLoading(false); }
  };
  useEffect(() => {
    let active = true;
    (async () => { await Promise.resolve(); if (active) loadSummaries(); })();
    return () => { active = false; };
  }, [q, dateFrom, dateTo]);

  const t = data?.totals || {};
  const maxOutcome = Math.max(1, ...((data?.outcomeBreakdown || []).map((b) => b.count)));

  return (
    <div data-testid="reports-panel">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-[13px] text-slate-500">Performance of your AI follow-up calls.</div>
        <div className="flex items-center gap-2">
          <select data-testid="reports-agent-select" value={agentId} onChange={(e) => setAgentId(e.target.value)}
            className="h-9 px-3 rounded-lg border border-slate-300 text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30">
            <option value="">All agents</option>
            {(agents || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
          <select data-testid="reports-range-select" value={days} onChange={(e) => setDays(Number(e.target.value))}
            className="h-9 px-3 rounded-lg border border-slate-300 text-[13.5px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500/30">
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 text-indigo-500 animate-spin" /></div>
      ) : (
        <>
          {/* AI-sourced revenue — the headline "agent paying for itself" stat */}
          <div className="rounded-xl border border-gray-700 bg-gray-800 p-5 mb-5 flex items-center justify-between" data-testid="kpi-aiCollectedRevenue">
            <div>
              <div className="flex items-center gap-1.5 text-[11.5px] font-semibold uppercase tracking-wider text-white"><Sparkles className="h-4 w-4" /> AI-sourced revenue collected</div>
              <div className="text-[34px] font-extrabold text-emerald-400 leading-tight mt-1" data-testid="ai-revenue-value">{usd(t.aiCollectedRevenue)}</div>
              <div className="text-[12.5px] text-white mt-0.5">Paid on {t.aiInvoicesPaid ?? 0} of {t.aiInvoicesSent ?? 0} invoice{t.aiInvoicesSent === 1 ? "" : "s"} the agent sent · last {data?.rangeDays ?? days} days</div>
            </div>
            <div className="hidden sm:flex h-14 w-14 rounded-2xl bg-emerald-500/20 items-center justify-center"><Wallet className="h-7 w-7 text-emerald-400" /></div>
          </div>

          {/* KPI cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-5">
            {KPIS.map(({ k, label, icon: Icon, fmt, cls }) => (
              <div key={k} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`kpi-${k}`}>
                <Icon className="h-4 w-4 text-slate-400 mb-2" />
                <div className={`text-[22px] font-bold ${cls}`}>{fmt(t[k] ?? 0)}</div>
                <div className="text-[11.5px] text-slate-500">{label}</div>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-4 mb-5">
            {/* Trend */}
            <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-4" data-testid="reports-trend">
              <div className="flex items-center gap-2 mb-3 text-[13.5px] font-semibold text-slate-700"><BarChart3 className="h-4 w-4 text-indigo-500" /> Calls &amp; conversions</div>
              <ResponsiveContainer width="100%" height={240}>
                <AreaChart data={data?.daily || []} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gCalls" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366f1" stopOpacity={0.35} /><stop offset="100%" stopColor="#6366f1" stopOpacity={0} /></linearGradient>
                    <linearGradient id="gConv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                  <Area type="monotone" dataKey="calls" stroke="#6366f1" strokeWidth={2} fill="url(#gCalls)" name="Calls" />
                  <Area type="monotone" dataKey="conversions" stroke="#10b981" strokeWidth={2} fill="url(#gConv)" name="Conversions" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Outcome breakdown */}
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="reports-outcomes">
              <div className="text-[13.5px] font-semibold text-slate-700 mb-3">Call outcomes</div>
              {(data?.outcomeBreakdown || []).length === 0 ? (
                <div className="text-[12.5px] text-slate-400 py-8 text-center">No completed calls in this range.</div>
              ) : (
                <div className="space-y-2.5">
                  {data.outcomeBreakdown.map((b) => (
                    <div key={b.outcome} data-testid={`outcome-${b.outcome}`}>
                      <div className="flex items-center justify-between text-[12.5px] mb-1">
                        <span className="text-slate-600">{b.label}</span>
                        <span className="font-semibold text-slate-700">{b.count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                        <div className={`h-full rounded-full ${OUTCOME_COLORS[b.outcome] || "bg-slate-400"}`} style={{ width: `${(b.count / maxOutcome) * 100}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Agent performance */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="reports-agents">
            <div className="px-4 py-3 border-b border-slate-200 text-[13.5px] font-semibold text-slate-700">Agent performance</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-200 text-[11px] uppercase tracking-wider">
                    <th className="px-4 py-2.5 font-semibold">Agent</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Calls</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Conn. rate</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Conversions</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Conv. rate</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Appts</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.agentPerformance || []).length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No call activity yet.</td></tr>
                  ) : (
                    data.agentPerformance.map((a) => (
                      <tr key={a.agentId} className="border-b border-slate-100" data-testid={`agent-perf-${a.agentId}`}>
                        <td className="px-4 py-2.5 font-medium text-slate-800">{a.agentName}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{a.calls}</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{a.connectionRate}%</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{a.conversions}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-600 font-semibold">{a.conversionRate}%</td>
                        <td className="px-4 py-2.5 text-right text-slate-600">{a.appointments}</td>
                        <td className="px-4 py-2.5 text-right text-emerald-700 font-semibold">{usd(a.revenueClosed)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Call summaries — every inbound + outbound call, regardless of email settings */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden mt-5" data-testid="call-summaries-panel">
            <div className="px-4 py-3 border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2 text-[13.5px] font-semibold text-slate-700">
                <ListFilter className="h-4 w-4 text-indigo-500" /> Call Summaries
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Search phone, name, outcome…"
                    data-testid="call-summaries-search-input"
                    className="h-9 pl-8 pr-3 rounded-lg border border-slate-300 text-[13px] w-56 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                  />
                </div>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  data-testid="call-summaries-date-from"
                  className="h-9 px-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                />
                <span className="text-[12.5px] text-slate-400">to</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  data-testid="call-summaries-date-to"
                  className="h-9 px-2 rounded-lg border border-slate-300 text-[13px] focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-200 text-[11px] uppercase tracking-wider">
                    <th className="px-4 py-2.5 font-semibold">Date</th>
                    <th className="px-4 py-2.5 font-semibold">Time</th>
                    <th className="px-4 py-2.5 font-semibold">Direction</th>
                    <th className="px-4 py-2.5 font-semibold">Phone</th>
                    <th className="px-4 py-2.5 font-semibold">Customer</th>
                    <th className="px-4 py-2.5 font-semibold">Outcome</th>
                    <th className="px-4 py-2.5 font-semibold">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {summariesLoading ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center"><Loader2 className="h-5 w-5 text-indigo-400 animate-spin inline" /></td></tr>
                  ) : summaries.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No call summaries recorded yet.</td></tr>
                  ) : (
                    summaries.map((c) => (
                      <tr key={c.id} className="border-b border-slate-100" data-testid={`call-summary-row-${c.id}`}>
                        <td className="px-4 py-2.5 text-slate-600">{c.date}</td>
                        <td className="px-4 py-2.5 text-slate-600">{c.time}</td>
                        <td className="px-4 py-2.5">
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${c.direction === "inbound" ? "bg-sky-50 text-sky-700" : "bg-violet-50 text-violet-700"}`}>
                            {c.direction === "inbound" ? "Inbound" : "Outbound"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-600">{c.phone || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-800 font-medium">{c.customerName || "Not identified"}</td>
                        <td className="px-4 py-2.5 text-slate-600">{c.outcome || "—"}</td>
                        <td className="px-4 py-2.5 text-slate-500 max-w-xs truncate" title={c.notes}>{c.notes || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
